import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Users, Clock, ClipboardList, AlertTriangle, BarChart3, CreditCard, ArrowRight, TrendingUp, Shield, Bell, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { ContextualTour } from '../../components/ContextualTour';
import { API } from '../../config/apiBase';


const EMP_DASH_STEPS = [
    { target: '[data-testid="emp-dash-kpis"]', title: 'Indicadores do Dia', content: 'Acompanhe em tempo real quantos colaboradores estao presentes, ausentes e ajustes pendentes.' },
    { target: '[data-testid="emp-dash-resumo"]', title: 'Resumo Operacional', content: 'Veja registros de ponto, marcacoes fora do geofence e jornadas cadastradas de forma rapida.' },
];

export default function EmpresaDashboardPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [empresa, setEmpresa] = useState(null);
    const [ajustes, setAjustes] = useState([]);
    const [registros, setRegistros] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;
        const h = { 'Authorization': `Bearer ${token}` };
        Promise.all([
            fetch(`${API}/ponto/dashboard/extended`, { headers: h }).then(r => r.json()),
            fetch(`${API}/ponto/empresa`, { headers: h }).then(r => r.json()),
            fetch(`${API}/ponto/ajustes`, { headers: h }).then(r => r.json()),
            fetch(`${API}/ponto/registros`, { headers: h }).then(r => r.json()),
        ]).then(([s, e, aj, reg]) => {
            setStats(s); setEmpresa(e);
            setAjustes(Array.isArray(aj) ? aj : []);
            setRegistros(Array.isArray(reg) ? reg : []);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    const presRate = stats?.total_employees > 0 ? Math.round((stats.present_today / stats.total_employees) * 100) : 0;
    const pendingAj = ajustes.filter(a => a.status === 'pendente');
    const recentRegs = registros.slice(0, 6);
    const planDays = empresa?.plan_expires ? Math.ceil((new Date(empresa.plan_expires) - new Date()) / 86400000) : null;

    return (
        <div className="space-y-5" data-testid="empresa-dashboard">
            <ContextualTour pageKey="empresa-dashboard" steps={EMP_DASH_STEPS} />
            {/* Welcome Banner */}
            <div className="rounded-xl p-5 sm:p-6" style={{ background: 'linear-gradient(135deg, rgba(var(--sv-accent-rgb), 0.15) 0%, rgba(var(--sv-accent-rgb), 0.03) 100%)', border: '1px solid rgba(var(--sv-accent-rgb), 0.2)' }}>
                <div className="flex items-start justify-between">
                    <div>
                        <h2 className="text-lg sm:text-xl font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>
                            {new Date().getHours() < 12 ? 'Bom dia' : new Date().getHours() < 18 ? 'Boa tarde' : 'Boa noite'}, {user?.name?.split(' ')[0]}
                        </h2>
                        <p className="text-xs sm:text-sm mt-1" style={{ color: 'var(--muted)' }}>
                            {empresa?.name} — {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </p>
                    </div>
                    <span className="text-[10px] sm:text-xs px-2 py-1 rounded-full font-medium shrink-0" style={{ background: 'rgba(var(--sv-accent-rgb), 0.15)', color: 'var(--sv-gold-primary)' }}>
                        Plano {empresa?.plan?.charAt(0).toUpperCase()}{empresa?.plan?.slice(1)}
                    </span>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="emp-dash-kpis">
                {[
                    { label: 'Colaboradores', value: stats?.total_employees || 0, icon: Users, color: '#3B82F6', sub: `${stats?.gestores || 0} gestores` },
                    { label: 'Presentes Hoje', value: stats?.present_today || 0, icon: CheckCircle, color: '#10B981', sub: `${presRate}% da equipe` },
                    { label: 'Ausentes', value: stats?.absent_today || 0, icon: XCircle, color: '#EF4444', sub: 'no dia' },
                    { label: 'Ajustes Pendentes', value: stats?.pending_ajustes || 0, icon: ClipboardList, color: '#F59E0B', sub: 'aguardando' },
                ].map((c) => (
                    <div key={c.label} className="rounded-xl p-3.5 sm:p-4 transition-all hover:scale-[1.02]" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center gap-2 mb-1.5">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${c.color}15` }}>
                                <c.icon size={14} style={{ color: c.color }} />
                            </div>
                        </div>
                        <p className="text-xl sm:text-2xl font-bold" style={{ color: 'var(--text)' }}>{c.value}</p>
                        <p className="text-[10px] sm:text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{c.label}</p>
                        <p className="text-[10px]" style={{ color: c.color }}>{c.sub}</p>
                    </div>
                ))}
            </div>

            {/* Two columns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Resumo Operacional */}
                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="emp-dash-resumo">
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)' }}>Resumo Operacional</h3>
                    <div className="space-y-2.5">
                        {[
                            { label: 'Registros hoje', value: stats?.records_today || 0, icon: Clock, color: 'var(--sv-gold-primary)' },
                            { label: 'Fora do geofence', value: stats?.outside_geofence_today || 0, icon: Shield, color: stats?.outside_geofence_today > 0 ? '#EF4444' : '#10B981' },
                            { label: 'Marcações negadas', value: stats?.denied_today || 0, icon: AlertTriangle, color: stats?.denied_today > 0 ? '#F59E0B' : '#6B7280' },
                            { label: 'Jornadas cadastradas', value: stats?.total_jornadas || 0, icon: BarChart3, color: '#8B5CF6' },
                        ].map((item) => (
                            <div key={item.label} className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                                <div className="flex items-center gap-2">
                                    <item.icon size={13} style={{ color: item.color }} />
                                    <span className="text-xs sm:text-sm" style={{ color: 'var(--muted)' }}>{item.label}</span>
                                </div>
                                <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Status do Plano */}
                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)' }}>Status da Conta</h3>
                    <div className="space-y-2.5">
                        <div className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                            <span className="text-xs sm:text-sm" style={{ color: 'var(--muted)' }}>Plano</span>
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.15)', color: 'var(--sv-gold-primary)' }}>
                                {empresa?.plan?.charAt(0).toUpperCase()}{empresa?.plan?.slice(1)}
                            </span>
                        </div>
                        <div className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                            <span className="text-xs sm:text-sm" style={{ color: 'var(--muted)' }}>Colaboradores</span>
                            <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{stats?.total_employees || 0} / {stats?.max_employees || 10}</span>
                        </div>
                        <div className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                            <span className="text-xs sm:text-sm" style={{ color: 'var(--muted)' }}>Geofence</span>
                            <span className="text-xs font-medium" style={{ color: stats?.geofence_enabled ? '#10B981' : '#6B7280' }}>
                                {stats?.geofence_enabled ? 'Ativo' : 'Inativo'}
                            </span>
                        </div>
                        {planDays !== null && (
                            <div className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                                <span className="text-xs sm:text-sm" style={{ color: 'var(--muted)' }}>Vencimento</span>
                                <span className="text-xs font-medium" style={{ color: planDays < 30 ? '#F59E0B' : '#10B981' }}>
                                    {planDays} dias
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Atividade Recente */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Atividade Recente</h3>
                    <a href="/sistema/ponto" className="text-[10px] sm:text-xs" style={{ color: 'var(--sv-gold-primary)' }}>Ver tudo</a>
                </div>
                {recentRegs.length === 0 ? (
                    <p className="text-xs text-center py-6" style={{ color: 'var(--muted)' }}>Nenhuma atividade registrada ainda</p>
                ) : (
                    <div className="space-y-1.5">
                        {recentRegs.map((r, i) => (
                            <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                                <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: r.tipo === 'entrada' ? '#10B981' : '#EF4444' }} />
                                <span className="text-xs flex-1" style={{ color: 'var(--text)' }}>{r.tipo === 'entrada' ? 'Entrada' : 'Saída'}</span>
                                <span className="text-[10px]" style={{ color: 'var(--muted)' }}>
                                    {new Date(r.timestamp_server || r.timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Insights Automáticos */}
            <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="flex items-center gap-2 mb-3">
                    <TrendingUp size={14} style={{ color: 'var(--sv-gold-primary)' }} />
                    <span className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Insights do Dia</span>
                </div>
                <div className="space-y-2">
                    {presRate >= 80 && <InsightRow type="ok" text={`Excelente! ${presRate}% da equipe presente. Operacao estavel.`} to="/sistema/ponto" />}
                    {presRate > 0 && presRate < 50 && <InsightRow type="warn" text={`Atencao: apenas ${presRate}% da equipe presente hoje.`} to="/sistema/ponto" />}
                    {presRate === 0 && stats?.total_employees > 0 && <InsightRow type="info" text="Nenhum registro de ponto hoje. Acompanhe a frequencia da equipe." to="/sistema/ponto" />}
                    {pendingAj.length > 0 && <InsightRow type="warn" text={`${pendingAj.length} solicitação(oes) de ajuste pendente(s). Revise para manter o histórico correto.`} to="/sistema/empresa/solicitacoes" />}
                    {planDays !== null && planDays <= 7 && planDays > 0 && <InsightRow type="alert" text={`Seu plano expira em ${planDays} dia(s). Renove para evitar interrupcao.`} to="/sistema/empresa/assinatura" />}
                    {stats?.total_employees === 0 && <InsightRow type="info" text="Cadastre colaboradores para comecar a usar o ponto digital." to="/sistema/empresa/colaboradores" />}
                    {stats?.outside_geofence_today > 0 && <InsightRow type="warn" text={`${stats.outside_geofence_today} marcação(oes) fora do geofence hoje.`} to="/sistema/ponto" />}
                    {presRate >= 50 && presRate < 80 && <InsightRow type="ok" text={`${presRate}% presente. Situacao normal.`} to="/sistema/ponto" />}
                </div>
            </div>

            {/* Atalhos Rápidos */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {[
                    { label: 'Ponto Digital', href: '/sistema/ponto', icon: Clock, color: 'var(--sv-gold-primary)' },
                    { label: 'Equipe', href: '/sistema/empresa/colaboradores', icon: Users, color: '#3B82F6' },
                    { label: 'Solicitações', href: '/sistema/empresa/solicitações', icon: ClipboardList, color: '#F59E0B' },
                    { label: 'Performance', href: '/sistema/empresa/performance', icon: TrendingUp, color: '#10B981' },
                ].map((a) => (
                    <a key={a.label} href={a.href} className="flex items-center gap-2.5 p-3 rounded-xl transition-all hover:scale-[1.02]"
                        style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`shortcut-${a.label.toLowerCase().replace(/\s/g,'-')}`}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${a.color}12` }}>
                            <a.icon size={15} style={{ color: a.color }} />
                        </div>
                        <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>{a.label}</span>
                    </a>
                ))}
            </div>

            {/* Pendências */}
            {pendingAj.length > 0 && (
                <div className="rounded-xl p-4" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={14} style={{ color: '#F59E0B' }} />
                        <span className="text-xs font-semibold" style={{ color: '#F59E0B' }}>{pendingAj.length} solicitação(oes) pendente(s)</span>
                    </div>
                    <a href="/sistema/empresa/solicitacoes" className="text-[10px] underline" style={{ color: '#F59E0B' }}>Revisar agora</a>
                </div>
            )}

        </div>
    );
}


function InsightRow({ type, text, to }) {
    const colors = { ok: '#10B981', warn: '#F59E0B', alert: '#EF4444', info: '#6B7280' };
    const c = colors[type] || colors.info;
    const content = (
        <div className={`flex items-start gap-2 py-1.5 ${to ? 'cursor-pointer group' : ''}`} style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: c }} />
            <span className={`text-[11px] flex-1 ${to ? 'group-hover:underline' : ''}`} style={{ color: 'var(--muted)' }}>{text}</span>
            {to && <span className="text-[10px] shrink-0" style={{ color: c, opacity: 0.6 }}>→</span>}
        </div>
    );
    if (to) return <a href={to} className="block">{content}</a>;
    return content;
}
